import qs from 'qs'

import { HttpService, HttpServiceResponse } from '../shared'
import {
  ClientViewPage,
  ClientNoteAddEditAttributes,
  ClientNoteDeleteAttributes,
  ClientNotesPage,
  ClientMonitoringAddEditAttributes,
  ClientMonitoringDeleteAttributes,
  ClientMonitoringsPage,
  ClientRiskLevelsPage,
  ClientRiskLevelAddAttributes,
  ClientRiskLevelDeleteAttributes,
  ClientDelegateAddEditAttributes,
  ClientDelegateDeleteAttributes,
  ClientDelegatesPage,
  ClientSearchAttributes,
  ClientCheckDublicateParams,
  ClientGetRequiredFiledsParams,
  ClinetRelationsAddAttributes,
  ClinetRelationsPage,
  ClientRelationsDeleteAttributes,
  ClientAddToBlackListAttributes,
  ClientBlacklistPage,
  ClientRemoveFromBlackListAttributes,
  ClientWatchlistPage,
  ClientAddToWatchListAttributes,
  sendSMSParams,
  ClientAddRemovePermissionParams,
  ValidatePinCodeParams,
  ClinetAddIdDocParams,
  ClinetEditIdDocParams,
  ClientDeleteIdDocParams,
  ClientVerifySendSmsParams,
  ClientVerifySMSVerifyParams,
  SendNewPinSMSParams,
  ClientCheckReqFieldsParams,
  ClientTagAddParams,
  ClientDeleteTagParams,
} from './client.types'

export class ClientProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_view_api.php`
  }

  public getDetails<T>(
    clientId: number,
    clientHash: string,
    page: ClientViewPage,
    additionalParams?: { [key: string]: any },
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID: clientId,
      HashSum_ClientID_: clientHash,
      ...additionalParams,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientNotesProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_post_api.php`
  }

  public addEditNote<T>(
    { ClientID, HashSum_ClientID_, ...rest }: ClientNoteAddEditAttributes,
    page: ClientNotesPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }

  public deleteNote<T>({
    ClientID,
    HashSum_ClientID_,
    ...rest
  }: ClientNoteDeleteAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page: ClientNotesPage.Delete,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }
}

export class ClientMonitoringsProvider {
  private get url(): string {
    return `${this.baseUrl}/client_oper_post_api.php`
  }

  constructor(private http: HttpService, private baseUrl: string) {}

  public addEditMonitoring<T>(
    { ClientID, HashSum_ClientID_, ...rest }: ClientMonitoringAddEditAttributes,
    page: ClientMonitoringsPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }

  public deleteMonitoring<T>({
    ClientID,
    HashSum_ClientID_,
    ...rest
  }: ClientMonitoringDeleteAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page: ClientMonitoringsPage.Delete,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }
}

export class ClientRiskLevelsProvider {
  private get url(): string {
    return `${this.baseUrl}/client_oper_post_api.php`
  }

  constructor(private http: HttpService, private baseUrl: string) {}

  public addRiskLevel<T>({
    ClientID,
    HashSum_ClientID_,
    ...rest
  }: ClientRiskLevelAddAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page: ClientRiskLevelsPage.Add,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }

  public deleteRiskLevel<T>({
    ClientID,
    HashSum_ClientID_,
    ...rest
  }: ClientRiskLevelDeleteAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page: ClientRiskLevelsPage.Delete,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }
}

export class ClientDelegatesProvider {
  private get url(): string {
    return `${this.baseUrl}/client_oper_post_api.php`
  }

  constructor(private http: HttpService, private baseUrl: string) {}

  public addEditDelegate<T>(
    { ClientID, HashSum_ClientID_, ...rest }: ClientDelegateAddEditAttributes,
    page: ClientDelegatesPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }

  public deleteDelegate<T>({
    ClientID,
    HashSum_ClientID_,
    ...rest
  }: ClientDelegateDeleteAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page: ClientDelegatesPage.Delete,
      ClientID,
      HashSum_ClientID_,
    })

    return this.http.post(`${this.url}?${query}`, rest)
  }
}

export class ClientRelationsProvider {
  private get url(): string {
    return `${this.baseUrl}/client_oper_post_api.php`
  }

  constructor(private http: HttpService, private baseUrl: string) {}

  public addRelation<T>(
    {
      ID,
      hash,
      relatedClientID,
      relatedClientHash,
      clientRelType,
      relatedClientRelType,
      Node,
      cfield,
    }: ClinetRelationsAddAttributes,
    page: ClinetRelationsPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID: ID,
      HashSum_ClientID_: hash,
    })

    const body = {
      RLClientID: relatedClientID,
      HashSum_RLClientID_: relatedClientHash,
      RLTypeID: clientRelType,
      RLTypeID2: relatedClientRelType,
      NodeID: Node,
      CField1: cfield,
    }

    return this.http.post(`${this.url}?${query}`, body)
  }

  public deleteRelation<T>(
    attributes: ClientRelationsDeleteAttributes,
    page: ClinetRelationsPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      page,
      ClientID: attributes.ClientID,
      HashSum_ClientID_: attributes.clientHash,
    })

    const body = {
      ID: attributes.RelationID,
      RLClientID: attributes.RLClientID,
      HashSum_RLClientID_: attributes.RelationClientHash,
    }

    return this.http.post(`${this.url}?${query}`, body)
  }
}

export class ClientBlacklistProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public add<T>(attribues: ClientAddToBlackListAttributes, page: ClientBlacklistPage): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ClientID: attribues.ClientID,
      HashSum_ClientID_: attribues.HashSum_ClientID_,
      Comment: attribues.Comment,
      page,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public remove<T>(
    attribues: ClientRemoveFromBlackListAttributes,
    page: ClientBlacklistPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ClientID: attribues.ClientID,
      HashSum_ClientID_: attribues.HashSum_ClientID_,
      page,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientTagProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public add<T>(params: ClientTagAddParams): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ...params,
      page: ClientViewPage.AddTag,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public delete<T>(params: ClientDeleteTagParams): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ...params,
      page: ClientViewPage.TagDelete,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientWatchlistProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public add<T>(attribues: ClientAddToWatchListAttributes, page: ClientWatchlistPage): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ClientID: attribues.ClientID,
      HashSum_ClientID_: attribues.HashSum_ClientID_,
      Comment: attribues.Comment,
      page,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public remove<T>(
    attribues: ClientRemoveFromBlackListAttributes,
    page: ClientWatchlistPage,
  ): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({
      ClientID: attribues.ClientID,
      HashSum_ClientID_: attribues.HashSum_ClientID_,
      page,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientSearchProvider {
  private get url(): string {
    return `${this.baseUrl}/client_search_api.php`
  }

  constructor(private http: HttpService, private baseUrl: string) {}

  public clientSearch<T>(params: ClientSearchAttributes): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({ ...params })

    return this.http.post(`${this.url}?${query}`, undefined, {
      headers: {
        'Content-type': 'application/x-www-form-urlencoded',
      },
    })
  }
}

export class ClientAddFormDataProvider {
  constructor(private http: HttpService, private baseUrl: string) {}
  private get url(): string {
    return `${this.baseUrl}/client_add_api.php`
  }

  public clientAdd<T>({ action, ID, hash, ...rest }: any): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify({ action, ClientID: ID, HashSum_ClientID_: hash })
    const body = { ...rest }
    return this.http.post(`${this.url}?${query}`, body)
  }
}

export class ClientCheckDublicateProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_checkduplicate_api.php`
  }

  public clientCheckDublicate<T>(params: ClientCheckDublicateParams): Promise<HttpServiceResponse<T>> {
    const query = qs.stringify(params)
    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientReqFieldsProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  public checkReqFields(params: ClientCheckReqFieldsParams): Promise<HttpServiceResponse> {
    const url = `${this.baseUrl}/client_oper_api.php`
    const query = qs.stringify({
      page: 'checkreqfields',
      ...params,
    })
    return this.http.post(`${url}?${query}`)
  }

  public getClientReqFields(params: ClientGetRequiredFiledsParams): Promise<HttpServiceResponse> {
    const url = `${this.baseUrl}/client_reqfields_api.php`
    const query = qs.stringify(params)
    return this.http.post(`${url}?${query}`)
  }
}

export class ClientSelectsProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_lookups_api.php`
  }

  public getSelects(): Promise<HttpServiceResponse> {
    return this.http.post(this.url)
  }
}

export class ClientSmsprovider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public sendSMS(params: sendSMSParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'smssend',
      ClientID: params.ID,
      HashSum_ClientID_: params.hash,
      Comment: params.smsText,
      Phone: params.phoneNumber,
    })
    return this.http.post(`${this.url}?${query}`)
  }

  public sendNewPinSMS(params: SendNewPinSMSParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'newpincode',
      ClientID: params.ID,
      HashSum_ClientID_: params.hash,
      Phone: params.phoneNumber,
    })
    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientPermissionsProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public addPermission(params: ClientAddRemovePermissionParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'accesspermadd',
      ...params,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public deletePermission(params: ClientAddRemovePermissionParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'accesspermdelete',
      ...params,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientValidatePinProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public validatePinCode(params: ValidatePinCodeParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'mclpincodevalidate',
      ClientID: params.ID,
      HashSum_ClientID_: params.hash,
      Comment: params.code,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientIdDocProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_oper_api.php`
  }

  public addIdDoc(params: ClinetAddIdDocParams): Promise<HttpServiceResponse> {
    const {
      ClientID,
      hash,
      IDCardTypeID,
      IDCardIssuerID,
      IDCardIssuerTxt,
      IDNumber,
      IssueDate,
      ExpireDate,
      DFDoc,
      FileID,
      HashSum_FileID_,
    } = params

    const query = qs.stringify({
      page: 'iddocadd',
      ClientID,
      HashSum_ClientID_: hash,
      IDCardTypeID,
      IDCardIssuerID,
      IDCardIssuerTxt,
      IDNumber,
      IssueDate,
      ExpireDate,
      DFDoc: +DFDoc,
      FileID,
      HashSum_FileID_,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public editIdDoc(params: ClinetEditIdDocParams): Promise<HttpServiceResponse> {
    const {
      ID,
      ClientID,
      hash,
      IDCardTypeID,
      IDCardIssuerID,
      IDCardIssuerTxt,
      IDNumber,
      IssueDate,
      ExpireDate,
      DFDoc,
      FileID,
      HashSum_FileID_,
    } = params

    const query = qs.stringify({
      page: 'iddocedit',
      ID,
      ClientID,
      HashSum_ClientID_: hash,
      IDCardTypeID,
      IDCardIssuerID,
      IDCardIssuerTxt,
      IDNumber,
      IssueDate,
      ExpireDate,
      DFDoc: +DFDoc,
      FileID,
      HashSum_FileID_,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public deleteIdDoc(params: ClientDeleteIdDocParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      page: 'iddocdelete',
      ...params,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}

export class ClientContactVerifyProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/client_contact_verify_api.php`
  }

  public sendSMS(params: ClientVerifySendSmsParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      action: 'send_sms',
      PersonalNo: params.PersonalNo,
      CellPhone: params.CellPhone,
      ClientID: params.ClientID,
      HashSum_ClientID_: params.ClientHash,
    })

    return this.http.post(`${this.url}?${query}`)
  }

  public verifySMS(params: ClientVerifySMSVerifyParams): Promise<HttpServiceResponse> {
    const query = qs.stringify({
      action: 'verify_sms',
      PersonalNo: params.PersonalNo,
      CellPhone: params.CellPhone,
      Code: params.Code,
      ClientID: params.ClientID,
      HashSum_ClientID_: params.ClientHash,
    })

    return this.http.post(`${this.url}?${query}`)
  }
}
